/**
 * KDS Connection Hook
 * 
 * Provides robust real-time order updates for Kitchen Display System stations.
 * 
 * Features:
 * - WebSocket connection with auto-reconnect (exponential backoff)
 * - Fallback to polling when WebSocket unavailable
 * - Offline caching using localStorage
 * - Connection status indicators
 * - Sound notifications for new orders
 * 
 * Usage:
 * const { orders, isConnected, isOffline, connectionStatus, refetch } = useKDSConnection('barista');
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/utils/apiClient';

// Connection states
const ConnectionStatus = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  POLLING: 'polling',
  OFFLINE: 'offline',
  ERROR: 'error'
};

// WebSocket reconnect settings
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 30000; // 30 seconds
const MAX_RECONNECT_ATTEMPTS = 10;

// Polling settings
const POLL_INTERVAL = 5000; // 5 seconds
const OFFLINE_CHECK_INTERVAL = 10000; // 10 seconds

// Cache keys
const getCacheKey = (station) => `kds-cache-${station}`;
const getLastSyncKey = (station) => `kds-last-sync-${station}`;

export default function useKDSConnection(station) {
  const [orders, setOrders] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(ConnectionStatus.CONNECTING);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState(null);
  
  // Refs for WebSocket and timers
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const offlineCheckRef = useRef(null);
  
  // Audio notification ref
  const audioRef = useRef(null);
  
  // Track order count for new order detection
  const prevOrderCountRef = useRef(0);

  // Initialize audio for notifications
  useEffect(() => {
    // Try multiple audio formats for compatibility
    const audio = new Audio();
    const canPlayMp3 = audio.canPlayType('audio/mpeg');
    const canPlayWav = audio.canPlayType('audio/wav');
    
    if (canPlayMp3) {
      audioRef.current = new Audio('/sounds/chime.mp3');
    } else if (canPlayWav) {
      audioRef.current = new Audio('/sounds/new-order.wav');
    }
    
    if (audioRef.current) {
      audioRef.current.volume = 0.7;
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Autoplay blocked - user needs to interact first
        console.log('Audio autoplay blocked - user interaction required');
      });
    }
  }, []);

  // Save orders to cache
  const saveToCache = useCallback((ordersData) => {
    try {
      localStorage.setItem(getCacheKey(station), JSON.stringify(ordersData));
      localStorage.setItem(getLastSyncKey(station), new Date().toISOString());
    } catch (e) {
      console.warn('Failed to save KDS cache:', e);
    }
  }, [station]);

  // Load orders from cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(getCacheKey(station));
      const lastSyncTime = localStorage.getItem(getLastSyncKey(station));
      
      if (cached) {
        const parsedOrders = JSON.parse(cached);
        setOrders(parsedOrders);
        prevOrderCountRef.current = parsedOrders.length;
        
        if (lastSyncTime) {
          setLastSync(new Date(lastSyncTime));
        }
        
        return parsedOrders;
      }
    } catch (e) {
      console.warn('Failed to load KDS cache:', e);
    }
    return null;
  }, [station]);

  // Process incoming orders (detect new orders, update state)
  const processOrders = useCallback((newOrders) => {
    // Check for new orders
    if (newOrders.length > prevOrderCountRef.current) {
      playNotificationSound();
    }
    prevOrderCountRef.current = newOrders.length;
    
    setOrders(newOrders);
    setLastSync(new Date());
    saveToCache(newOrders);
    setError(null);
  }, [playNotificationSound, saveToCache]);

  // Fetch orders via REST API (polling fallback)
  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get(`/api/kds/station/${station}`);
      const newOrders = response.data.orders || [];
      processOrders(newOrders);
      
      // If we were offline, we're back online
      if (isOffline) {
        setIsOffline(false);
      }
      
      return newOrders;
    } catch (err) {
      console.error(`KDS fetch error (${station}):`, err);
      setError(err.message);
      
      // If network error, switch to offline mode
      if (!navigator.onLine || err.code === 'ERR_NETWORK') {
        setIsOffline(true);
        setConnectionStatus(ConnectionStatus.OFFLINE);
        loadFromCache();
      }
      
      return null;
    }
  }, [station, processOrders, isOffline, loadFromCache]);

  // Get WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/api/kds/ws/${station}`;
  }, [station]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    setConnectionStatus(ConnectionStatus.CONNECTING);
    
    try {
      const wsUrl = getWebSocketUrl();
      console.log(`KDS: Connecting to WebSocket ${wsUrl}`);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log(`KDS: WebSocket connected (${station})`);
        setConnectionStatus(ConnectionStatus.CONNECTED);
        reconnectAttemptsRef.current = 0;
        setError(null);
        
        // Stop polling since WebSocket is connected
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        // Send initial handshake
        ws.send(JSON.stringify({ type: 'subscribe', station }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'orders_update') {
            processOrders(data.orders || []);
          } else if (data.type === 'new_order') {
            // New order notification - refetch full list
            fetchOrders();
            playNotificationSound();
          } else if (data.type === 'item_ready') {
            // Item marked ready - refetch
            fetchOrders();
          } else if (data.type === 'ping') {
            // Respond to server ping
            ws.send(JSON.stringify({ type: 'pong' }));
          }
        } catch (e) {
          console.error('KDS: WebSocket message parse error:', e);
        }
      };

      ws.onerror = (err) => {
        console.error(`KDS: WebSocket error (${station}):`, err);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log(`KDS: WebSocket closed (${station}) - Code: ${event.code}`);
        wsRef.current = null;
        
        // Attempt reconnect if not intentionally closed
        if (event.code !== 1000) {
          attemptReconnect();
        }
      };

    } catch (err) {
      console.error('KDS: Failed to create WebSocket:', err);
      setError(err.message);
      startPolling();
    }
  }, [station, getWebSocketUrl, processOrders, fetchOrders, playNotificationSound]);

  // Attempt WebSocket reconnect with exponential backoff
  const attemptReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log('KDS: Max reconnect attempts reached, falling back to polling');
      setConnectionStatus(ConnectionStatus.POLLING);
      startPolling();
      return;
    }

    setConnectionStatus(ConnectionStatus.RECONNECTING);
    
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current),
      MAX_RECONNECT_DELAY
    );
    
    console.log(`KDS: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${MAX_RECONNECT_ATTEMPTS})`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connectWebSocket();
    }, delay);
  }, [connectWebSocket]);

  // Start polling fallback
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return; // Already polling
    
    console.log(`KDS: Starting polling mode (${station})`);
    setConnectionStatus(ConnectionStatus.POLLING);
    
    // Initial fetch
    fetchOrders();
    
    // Set up polling interval
    pollIntervalRef.current = setInterval(fetchOrders, POLL_INTERVAL);
  }, [station, fetchOrders]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('KDS: Network back online');
      setIsOffline(false);
      
      // Try to reconnect WebSocket
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectAttemptsRef.current = 0;
        connectWebSocket();
      }
    };

    const handleOffline = () => {
      console.log('KDS: Network offline');
      setIsOffline(true);
      setConnectionStatus(ConnectionStatus.OFFLINE);
      
      // Load cached data
      loadFromCache();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connectWebSocket, loadFromCache]);

  // Initialize connection
  useEffect(() => {
    // Load cache first for instant display
    loadFromCache();
    
    // Check if online
    if (!navigator.onLine) {
      setIsOffline(true);
      setConnectionStatus(ConnectionStatus.OFFLINE);
      return;
    }

    // Try WebSocket first, fall back to polling
    // Note: WebSocket endpoint may not exist yet, so start with polling
    startPolling();
    
    // Cleanup
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (offlineCheckRef.current) {
        clearInterval(offlineCheckRef.current);
      }
    };
  }, [station, loadFromCache, startPolling]);

  // Manual refetch
  const refetch = useCallback(() => {
    return fetchOrders();
  }, [fetchOrders]);

  // Clear cache (for debugging/reset)
  const clearCache = useCallback(() => {
    localStorage.removeItem(getCacheKey(station));
    localStorage.removeItem(getLastSyncKey(station));
  }, [station]);

  return {
    orders,
    isConnected: connectionStatus === ConnectionStatus.CONNECTED || connectionStatus === ConnectionStatus.POLLING,
    isOffline,
    connectionStatus,
    lastSync,
    error,
    refetch,
    clearCache
  };
}

// Export connection status enum for consumers
export { ConnectionStatus };
